import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { router } from '@inertiajs/react';
import { X, Upload, FileText } from 'lucide-react';
import { getCSRFToken } from '../../utils/csrf';
import type { PendingItem } from '../../types/accountReceivable';

interface RegisterPaymentModalProps {
  accountId: number;
  onClose: () => void;
}

const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

export function RegisterPaymentModal({ accountId, onClose }: RegisterPaymentModalProps) {
  const [items, setItems] = useState<PendingItem[]>([]);
  const [amounts, setAmounts] = useState<Record<string, number>>({});
  const [remaining, setRemaining] = useState(0);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  // Fetch pending items
  useEffect(() => {
    fetch(`/cuenta-corriente/${accountId}/pendientes`, {
      credentials: 'include',
      headers: { 'Accept': 'application/json', 'X-XSRF-TOKEN': getCSRFToken() },
    })
      .then((res) => {
        if (!res.ok) throw new Error('No se pudieron cargar los movimientos pendientes');
        return res.json();
      })
      .then((data) => {
        setItems(data.items);
        const initialAmounts: Record<string, number> = {};
        data.items.forEach((item: PendingItem) => {
          initialAmounts[`${item.type}_${item.id}`] = 0;
        });
        setAmounts(initialAmounts);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [accountId]);

  const total = useMemo(() => {
    const itemsTotal = Object.values(amounts).reduce((sum, v) => sum + (v || 0), 0);
    return itemsTotal + (remaining || 0);
  }, [amounts, remaining]);

  const canSubmit = total > 0 && file !== null && !submitting;

  const setItemAmount = (key: string, value: number) => {
    setAmounts((prev) => ({ ...prev, [key]: value }));
  };

  const assignFull = (item: PendingItem) => {
    setItemAmount(`${item.type}_${item.id}`, item.balance);
  };

  const assignAll = () => {
    const newAmounts: Record<string, number> = {};
    items.forEach((item) => {
      newAmounts[`${item.type}_${item.id}`] = item.balance;
    });
    setAmounts(newAmounts);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    const payload = new FormData();
    payload.append('date', date);
    if (description.trim()) payload.append('description', description.trim());
    if (file) payload.append('transfer_file', file);
    if (remaining > 0) payload.append('remaining', String(remaining));

    const itemsPayload = items
      .map((item) => ({
        type: item.type,
        id: item.id,
        amount: amounts[`${item.type}_${item.id}`] || 0,
      }))
      .filter((i) => i.amount > 0);

    payload.append('items', JSON.stringify(itemsPayload));

    try {
      const res = await fetch(`/cuenta-corriente/${accountId}/recibo`, {
        method: 'POST',
        body: payload,
        credentials: 'include',
        headers: {
          'X-XSRF-TOKEN': getCSRFToken(),
          'Accept': 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.message || json?.error || 'Error al registrar el pago');
      }

      onClose();
      router.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-[fadeIn_150ms_ease-out]"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-xl bg-white shadow-xl animate-[slideUp_200ms_ease-out]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-800">Registrar pago</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <svg className="h-6 w-6 animate-spin text-green-500" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25" />
                <path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-600">{error}</div>
          )}

          {!loading && (
            <>
              {/* Date + Description */}
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Fecha</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Descripcion <span className="text-gray-400">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Ej: Transferencia bancaria"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </div>
              </div>

              {/* Pending items table */}
              {items.length > 0 ? (
                <>
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      Seleccioná los movimientos que desees abonar.
                    </p>
                    <button
                      type="button"
                      onClick={assignAll}
                      className="rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200"
                    >
                      Asignar todos
                    </button>
                  </div>
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          <th className="px-3 py-2 text-left">Concepto</th>
                          <th className="hidden px-3 py-2 text-left sm:table-cell">Fecha</th>
                          <th className="px-3 py-2 text-right">Saldo</th>
                          <th className="px-3 py-2 text-right">Paga</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => {
                          const key = `${item.type}_${item.id}`;
                          const amount = amounts[key] || 0;
                          return (
                            <tr key={key} className="border-t border-gray-100">
                              <td className="px-3 py-2 text-gray-800">{item.label}</td>
                              <td className="hidden px-3 py-2 text-gray-500 sm:table-cell">{item.date}</td>
                              <td className="px-3 py-2 text-right text-gray-600">
                                {formatCurrency(item.balance)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <input
                                    type="number"
                                    value={amount || ''}
                                    onChange={(e) =>
                                      setItemAmount(key, Math.min(parseFloat(e.target.value) || 0, item.balance))
                                    }
                                    min={0}
                                    max={item.balance}
                                    step="1"
                                    className="w-24 rounded border border-gray-300 px-2 py-1 text-right text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => assignFull(item)}
                                    title="Asignar total"
                                    className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                                  >
                                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {/* Entrega a cuenta */}
                        <tr className="border-t border-gray-200 bg-gray-50">
                          <td className="px-3 py-2 text-gray-700" colSpan={3}>
                            Entrega a cuenta de futuros pedidos
                          </td>
                          <td className="px-3 py-2 text-right">
                            <input
                              type="number"
                              value={remaining || ''}
                              onChange={(e) => setRemaining(parseFloat(e.target.value) || 0)}
                              min={0}
                              step="1"
                              className="w-24 rounded border border-gray-300 px-2 py-1 text-right text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="mb-4 rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500">
                  No hay movimientos pendientes. Podés registrar una entrega a cuenta.
                  <div className="mt-3">
                    <label className="text-sm font-medium text-gray-700">Monto a cuenta</label>
                    <input
                      type="number"
                      value={remaining || ''}
                      onChange={(e) => setRemaining(parseFloat(e.target.value) || 0)}
                      min={0}
                      step="0.01"
                      className="mx-auto mt-1 block w-32 rounded border border-gray-300 px-2 py-1 text-right text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                    />
                  </div>
                </div>
              )}

              {/* Total */}
              <div className="mt-3 flex justify-end border-t border-gray-100 pt-3">
                <span className="text-base font-semibold text-gray-800">
                  Total: {formatCurrency(total)}
                </span>
              </div>

              {/* File upload */}
              <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="mb-2 text-sm font-semibold text-gray-700">
                  Comprobante de transferencia <span className="text-red-500">*</span>
                </h3>
                {file ? (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FileText className="h-4 w-4 text-green-500" />
                    <span className="font-medium">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="ml-1 rounded p-0.5 text-gray-400 hover:text-red-500"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-green-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-green-600">
                    <Upload className="h-3.5 w-3.5" />
                    Subir comprobante
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      onChange={handleFileChange}
                    />
                  </label>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-lg bg-green-500 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Registrando...' : 'Registrar pago'}
          </button>
        </div>
      </div>
    </div>
  );
}
